// this entire file should be removed once the business no longer requires it

import { useCallback } from 'react';

import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import WavefrontReporter from 'reporting/wavefront/WavefrontReporter';
import { frictionlessLoginSessionIdSelector } from 'store/auth/selectors';
import { insightSelectors } from 'store/insight/selectors';
import { useSelector } from 'store/utils';
import { ContextualParams } from 'types/SplunkLogs';

const splunkReporter = SplunkReporter.getInstance();
const wavefrontReporter = WavefrontReporter.getInstance();

const logger = 'frictionlessLogin';

type FrictionlessErrorIssue = 'wallet' | 'identity';

interface FrictionlessLog {
  message: string;
  extraParams?: Record<string, string | boolean | number>;
  event?: ContextualParams['event'];
  logLevel?: ContextualParams['logInfo']['logLevel'];
  rum?: ContextualParams['rum'];
  error?: Error;
}

// in an external function as we may want the option to call this directly
const logFrictionlessEvent = ({
  token,
  logLevel = 'info',
  event = 'report',
  error,
  extraParams,
  sessionId,
  message,
  rum,
}: FrictionlessLog & { token: string; sessionId?: string }) => {
  splunkReporter.contextual({
    action: 'frictionlessLogin',
    activityInfo: {
      token,
      message,
      frictionlessSessionId: sessionId,
      ...extraParams,
    },
    event,
    logInfo: {
      logLevel,
      logType: 'contextual',
      logger,
    },
    error,
    rum,
  });
};

export const useReportFrictionlessEvent = () => {
  const token = useSelector((state) => insightSelectors.tokenSelector(state.insight));
  const sessionId = useSelector(frictionlessLoginSessionIdSelector);

  const log = useCallback(
    (params: FrictionlessLog) => {
      logFrictionlessEvent({
        ...params,
        token,
        sessionId,
      });
    },
    [token, sessionId]
  );

  const trackLoginOpened = useCallback(async () => {
    try {
      await wavefrontReporter.trackFrictionlessLoginOpened(token);
      log({
        message: 'Frictionless login dialog opened',
        event: 'report',
      });
    } catch (error) {
      log({
        message: 'Failed to track frictionless login opened',
        error: error as Error,
        logLevel: 'error',
      });
    }
  }, [log]);

  const trackSignupViewed = useCallback(async () => {
    try {
      await wavefrontReporter.trackFrictionlessSignupViewed(token);
      log({
        message: 'Frictionless signup viewed',
        event: 'report',
      });
    } catch (error) {
      log({
        message: 'Failed to track frictionless signup viewed',
        error: error as Error,
        logLevel: 'error',
      });
    }
  }, [log]);

  const trackSignupError = useCallback(
    async (issue: FrictionlessErrorIssue) => {
      try {
        await wavefrontReporter.trackFrictionlessSignupError(issue, token);
        log({
          message: 'Frictionless signup error',
          extraParams: { issue },
          event: 'report',
          logLevel: 'error',
        });
      } catch (error) {
        log({
          message: 'Failed to track frictionless signup error',
          error: error as Error,
          logLevel: 'error',
        });
      }
    },
    [log]
  );

  const trackLoginError = useCallback(
    async (issue: FrictionlessErrorIssue) => {
      try {
        await wavefrontReporter.trackFrictionlessLoginError(issue, token);
        log({
          message: 'Frictionless login error',
          extraParams: { issue },
          event: 'report',
          logLevel: 'error',
        });
      } catch (error) {
        log({
          message: 'Failed to track frictionless login error',
          error: error as Error,
          logLevel: 'error',
        });
      }
    },
    [log]
  );

  const trackSignInCTAViewed = useCallback(async () => {
    try {
      await wavefrontReporter.trackSignInCTAViewed(token);
      log({
        message: 'Frictionless sign in CTA viewed',
        event: 'report',
      });
    } catch (error) {
      log({
        message: 'Failed to track frictionless sign in CTA viewed',
        error: error as Error,
        logLevel: 'error',
      });
    }
  }, [log]);

  const trackSignInCTAClicked = useCallback(async () => {
    try {
      await wavefrontReporter.trackSignInCTAClicked(token);
      log({
        message: 'Frictionless sign in CTA clicked',
        event: 'report',
      });
    } catch (error) {
      log({
        message: 'Failed to track frictionless sign in CTA clicked',
        error: error as Error,
        logLevel: 'error',
      });
    }
  }, [log]);

  const trackFrictionlessPhase3IdentityWidgetCanceled = useCallback(async () => {
    try {
      await wavefrontReporter.trackFrictionlessPhase3IdentityWidgetCanceled(token);
      log({
        message: 'Frictionless phase 3 identity widget canceled',
        event: 'report',
      });
    } catch (error) {
      log({
        message: 'Failed to track frictionless phase 3 identity widget canceled',
        error: error as Error,
        logLevel: 'error',
      });
    }
  }, [log]);

  const trackFrictionlessPhase3IdentityWidgetCompleted = useCallback(async () => {
    try {
      await wavefrontReporter.trackFrictionlessPhase3IdentityWidgetCompleted(token);
      log({
        message: 'Frictionless phase 3 identity widget completed',
        event: 'report',
      });
    } catch (error) {
      log({
        message: 'Failed to track frictionless phase 3 identity widget completed',
        error: error as Error,
        logLevel: 'error',
      });
    }
  }, [log]);

  const trackFrictionlessPhase3IdentityWidgetError = useCallback(async () => {
    try {
      await wavefrontReporter.trackFrictionlessPhase3IdentityWidgetError(token);
      log({
        message: 'Frictionless phase 3 identity widget error',
        extraParams: { issue: 'identity' },
        event: 'report',
        logLevel: 'error',
      });
    } catch (error) {
      log({
        message: 'Failed to track frictionless phase 3 identity widget error',
        error: error as Error,
        logLevel: 'error',
      });
    }
  }, [log]);

  return {
    log,
    trackLoginOpened,
    trackSignupViewed,
    trackSignupError,
    trackLoginError,
    trackSignInCTAViewed,
    trackSignInCTAClicked,
    trackFrictionlessPhase3IdentityWidgetCanceled,
    trackFrictionlessPhase3IdentityWidgetCompleted,
    trackFrictionlessPhase3IdentityWidgetError,
  };
};
